/**
 * @file BuddySideBar.tsx
 * @description This file contains the implementation of the BuddySideBar component.
 * @description It displays a sidebar with items and a drawer.
 */

import { Box, Drawer, Fab } from '@mui/material'
import ProactivesIcon from 'components/icons/ProactivesIcon'
import config from 'config'
import React from 'react'
import { Outlet, useMatch } from 'react-router-dom'
import { proactivesPageURL } from 'routes/routes'
import { SideBar } from '../components/Sidebar'
import './BuddySideBar.scss'
import AddIcon from '@mui/icons-material/Add'
import supportIcon from '../assets/images/support-icon.svg'

/**
 * Represents the props for a sidebar item.
 */
type SideBarItemProps = {
    title: string
    tooltip: string
    active?: boolean
    to?: string
    onClick?: () => void
    icon?: JSX.Element
    visible?: boolean
    childElement?: Array<SideBarItemProps>
    open?: boolean
    url?: string
}

/**
 * Represents the BuddySideBar component.
 */
const BuddySideBar: React.FC = () => {
    const matchProactives = useMatch(`${config.app.basePath}/agent/`)
    const matchCallSession = useMatch(
        `${config.app.basePath}/agent/callsession/:customer_id`
    )

    const [drawerVisible, setDrawerVisible] = React.useState(false)
    const isProactivesPage = matchProactives !== null
    const isCallSessionPage = matchCallSession !== null

    const items: SideBarItemProps[] = [
        {
            title: 'Recommendations',
            icon: (
                <ProactivesIcon
                    id="application-icon-opportunities"
                    active={isProactivesPage || isCallSessionPage}
                />
            ),
            active: isProactivesPage || isCallSessionPage,
            url: proactivesPageURL(),
            tooltip: 'List of Recommendations',
            visible: true,
        },
    ]

    return (
        <Box
            flex={1}
            display="flex"
            flexDirection="row"
            height="100%"
            sx={{ overflow: 'hidden', w: 10 }}
        >
            <SideBar items={items} sidebarType="buddy" />
            <Box flex={1} display="flex" flexDirection="column">
                <Outlet />
                <a
                    href="https://help.barcodesgroup.com/portal/en/newticket"
                    target="_blank"
                >
                    <Fab
                        size="small"
                        color="secondary"
                        aria-label="add"
                        sx={{
                            position: 'fixed',
                            bottom: 8,
                            right: 18,
                        }}
                    >
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M11.75 19H11.5C9.13333 19 7.125 18.175 5.475 16.525C3.825 14.875 3 12.8667 3 10.5C3 8.13333 3.825 6.125 5.475 4.475C7.125 2.825 9.13333 2 11.5 2C12.6833 2 13.7877 2.22067 14.813 2.662C15.8383 3.10333 16.7383 3.71167 17.513 4.487C18.2877 5.26233 18.8957 6.16233 19.337 7.187C19.7783 8.21167 19.9993 9.316 20 10.5C20 12.7333 19.3707 14.8083 18.112 16.725C16.8533 18.6417 15.266 20.1417 13.35 21.225C13.1833 21.3083 13.0167 21.3543 12.85 21.363C12.6833 21.3717 12.5333 21.334 12.4 21.25C12.2667 21.1667 12.15 21.0583 12.05 20.925C11.95 20.7917 11.8917 20.6333 11.875 20.45L11.75 19ZM14 18.35C15.1833 17.35 16.146 16.1793 16.888 14.838C17.63 13.4967 18.0007 12.0507 18 10.5C18 8.68333 17.3707 7.146 16.112 5.888C14.8533 4.63 13.316 4.00067 11.5 4C9.68333 4 8.146 4.62933 6.888 5.888C5.63 7.14667 5.00067 8.684 5 10.5C5 12.3167 5.62933 13.8543 6.888 15.113C8.14667 16.3717 9.684 17.0007 11.5 17H14V18.35ZM11.475 15.975C11.7583 15.975 12 15.875 12.2 15.675C12.4 15.475 12.5 15.2333 12.5 14.95C12.5 14.6667 12.4 14.425 12.2 14.225C12 14.025 11.7583 13.925 11.475 13.925C11.1917 13.925 10.95 14.025 10.75 14.225C10.55 14.425 10.45 14.6667 10.45 14.95C10.45 15.2333 10.55 15.475 10.75 15.675C10.95 15.875 11.1917 15.975 11.475 15.975ZM9.3 8.375C9.48333 8.45833 9.66667 8.46267 9.85 8.388C10.0333 8.31333 10.1833 8.19233 10.3 8.025C10.45 7.825 10.625 7.67067 10.825 7.562C11.025 7.45333 11.25 7.39933 11.5 7.4C11.9 7.4 12.225 7.51233 12.475 7.737C12.725 7.96167 12.85 8.24933 12.85 8.6C12.85 8.81667 12.7877 9.03333 12.663 9.25C12.5383 9.46667 12.3173 9.73333 12 10.05C11.5833 10.4167 11.275 10.7627 11.075 11.088C10.875 11.4133 10.775 11.7423 10.775 12.075C10.775 12.275 10.846 12.446 10.988 12.588C11.13 12.73 11.3007 12.8007 11.5 12.8C11.7 12.8 11.8667 12.725 12 12.575C12.1333 12.425 12.2333 12.25 12.3 12.05C12.3833 11.7667 12.5333 11.5083 12.75 11.275C12.9667 11.0417 13.1667 10.8333 13.35 10.65C13.7 10.3 13.9627 9.95 14.138 9.6C14.3133 9.25 14.4007 8.9 14.4 8.55C14.4 7.78333 14.1373 7.16667 13.612 6.7C13.0867 6.23333 12.3827 6 11.5 6C10.9667 6 10.475 6.12933 10.025 6.388C9.575 6.64667 9.20833 7.00067 8.925 7.45C8.825 7.63333 8.81233 7.81267 8.887 7.988C8.96167 8.16333 9.09933 8.29233 9.3 8.375Z"
                                fill="white"
                            />
                        </svg>
                    </Fab>
                </a>
            </Box>
            <Drawer
                open={drawerVisible}
                onClose={() => setDrawerVisible(false)}
                anchor="right"
                ModalProps={{
                    keepMounted: true,
                }}
            ></Drawer>
        </Box>
    )
}

export default BuddySideBar
